'use client';

import { useState } from 'react';

import { cn } from '../../lib/utils';
import { textInputStyles } from './text-input';

const HEX = /^#[0-9a-fA-F]{6}$/;

export type ColorFieldProps = {
  /** `#RRGGBB`. */
  value: string;
  onValueChange: (value: string) => void;
  /** Names both the swatch and the hex box, e.g. "Text colour". */
  'aria-label': string;
  size?: 'sm' | 'md';
  className?: string;
};

/**
 * A colour: the system picker behind a swatch, and the hex code to type or
 * paste. The hex applies once it's a full `#RRGGBB`; anything else goes back
 * to the last good colour on blur.
 */
export function ColorField({
  value,
  onValueChange,
  'aria-label': label,
  size = 'md',
  className,
}: ColorFieldProps) {
  const [draft, setDraft] = useState(value.toUpperCase());
  // A new value from outside (the picker, a preset) replaces what's typed.
  const [shown, setShown] = useState(value);
  if (value !== shown) {
    setShown(value);
    setDraft(value.toUpperCase());
  }
  const styles = textInputStyles({ size });

  return (
    <div className={styles.root({ className: cn('gap-2 pl-1.5', className) })}>
      <input
        type="color"
        aria-label={`${label}, picker`}
        value={HEX.test(value) ? value : '#000000'}
        onChange={(event) => onValueChange(event.target.value.toUpperCase())}
        className="border-border-subtle size-6 shrink-0 cursor-pointer overflow-hidden rounded-sm border bg-transparent p-0 [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
      />
      <input
        aria-label={label}
        value={draft}
        maxLength={7}
        spellCheck={false}
        onChange={(event) => {
          const next = event.target.value.startsWith('#')
            ? event.target.value
            : `#${event.target.value}`;
          setDraft(next.toUpperCase());
          if (HEX.test(next)) onValueChange(next.toUpperCase());
        }}
        onBlur={() => setDraft(value.toUpperCase())}
        className="text-data-md min-w-0 flex-1 bg-transparent outline-none"
      />
    </div>
  );
}
